import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FiLoader } from 'react-icons/fi';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import CustomModal from '../Modals/CustomModal';
import api from '../../services/api';
import {
  DetailsGrid,
  DetailRow,
  DetailLabel,
  DetailValue,
  StatusIndicator,
  LoadingContainer,
  ErrorContainer,
  ModalFooter,
  CloseButton
} from './JobDetailsModal.styles';

const JobDetailsModal = ({ isOpen, onClose, jobId }) => {
  const [jobDetails, setJobDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && jobId) {
      fetchJobDetails();
    }
  }, [isOpen, jobId]);

  const fetchJobDetails = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/scheduler/jobs/${jobId}`);
      setJobDetails(response.data);
    } catch (err) {
      console.error('Erro ao buscar detalhes do job:', err);
      setError('Não foi possível carregar os detalhes do job.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'Não disponível';
    try {
      return format(parseISO(dateTime), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
    } catch (error) {
      return dateTime;
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <LoadingContainer>
          <FiLoader size={32} color="#2f2f2f" />
          <p>Carregando detalhes do job...</p>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorContainer>
          <p>{error}</p>
        </ErrorContainer>
      );
    }

    if (!jobDetails) return null;

    return (
      <DetailsGrid>
        <DetailRow>
          <DetailLabel>ID:</DetailLabel>
          <DetailValue>
            <code>{jobDetails.id}</code>
          </DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Nome:</DetailLabel>
          <DetailValue>{jobDetails.name}</DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Status Geral:</DetailLabel>
          <DetailValue>
            <StatusIndicator $enabled={jobDetails.enabled}>
              {jobDetails.enabled ? '✓ Ativo' : '✗ Inativo'}
            </StatusIndicator>
          </DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Pausado (temporário):</DetailLabel>
          <DetailValue>
            <StatusIndicator $enabled={!jobDetails.next_run_time}>
              {!jobDetails.next_run_time ? '✓ Sim (volta após reiniciar)' : '✗ Não'}
            </StatusIndicator>
          </DetailValue>
        </DetailRow>

        {jobDetails.description && (
          <DetailRow>
            <DetailLabel>Descrição:</DetailLabel>
            <DetailValue>{jobDetails.description}</DetailValue>
          </DetailRow>
        )}

        <DetailRow>
          <DetailLabel>Próxima Execução:</DetailLabel>
          <DetailValue>{formatDateTime(jobDetails.next_run_time)}</DetailValue>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Gatilho:</DetailLabel>
          <DetailValue>
            <code>{jobDetails.trigger}</code>
          </DetailValue>
        </DetailRow>

      </DetailsGrid>
    );
  };

  const footer = (
    <ModalFooter>
      <CloseButton onClick={onClose}>Fechar</CloseButton>
    </ModalFooter>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalhes do Job"
      footer={footer}
    >
      {renderContent()}
    </CustomModal>
  );
};

JobDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  jobId: PropTypes.string,
};

JobDetailsModal.defaultProps = {
  jobId: null,
};

export default JobDetailsModal;
